import type { BoardDoc } from '../doc/BoardDoc';
import { DEFAULT_NOTE_SIZE, MIN_ITEM_SIZE, NOTE_COLORS, type NoteColor, type NoteItem, type TextAlign, type VerticalAlign } from '../doc/schema';
import { textStyleOf } from './text/textStyle';

/**
 * How a new sticky note looks: paper, text style and size. A board can keep a default of its own
 * in `meta.noteLook` (set from a note's inspector, synced and shared by everyone on the board);
 * without one, notes start as plain yellow squares.
 */
export interface NoteLook {
  color: NoteColor;
  fontSize: number;
  font: string | null;
  textColor: string | null;
  bold: boolean;
  italic: boolean;
  align: TextAlign;
  valign: VerticalAlign;
  w: number;
  h: number;
}

export const NOTE_LOOK_KEY = 'noteLook';

export const BUILTIN_NOTE_LOOK: NoteLook = {
  color: NOTE_COLORS[0],
  fontSize: 20,
  font: null,
  textColor: null,
  bold: false,
  italic: false,
  align: 'left',
  valign: 'top',
  w: DEFAULT_NOTE_SIZE,
  h: DEFAULT_NOTE_SIZE,
};

const MAX_SIDE = 4000;

/** A stored default, checked field by field (another client version may have written it); built-in values fill the gaps. */
export function parseNoteLook(raw: unknown): NoteLook {
  const b = BUILTIN_NOTE_LOOK;
  if (!raw || typeof raw !== 'object') return b;
  const r = raw as Record<string, unknown>;
  const num = (v: unknown, min: number, max: number, fallback: number) =>
    typeof v === 'number' && Number.isFinite(v) ? Math.min(max, Math.max(min, v)) : fallback;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : null);
  return {
    color: (NOTE_COLORS as readonly unknown[]).includes(r.color) ? (r.color as NoteColor) : b.color,
    fontSize: num(r.fontSize, 4, 400, b.fontSize),
    font: str(r.font),
    textColor: str(r.textColor),
    bold: r.bold === true,
    italic: r.italic === true,
    align: r.align === 'center' || r.align === 'right' ? r.align : 'left',
    valign: r.valign === 'middle' || r.valign === 'bottom' ? r.valign : 'top',
    w: num(r.w, MIN_ITEM_SIZE, MAX_SIDE, b.w),
    h: num(r.h, MIN_ITEM_SIZE, MAX_SIDE, b.h),
  };
}

/** The look new notes on this board get. */
export function boardNoteLook(board: BoardDoc): NoteLook {
  return parseNoteLook(board.meta.get(NOTE_LOOK_KEY));
}

export function lookOfNote(item: NoteItem): NoteLook {
  const s = textStyleOf(item);
  return {
    color: item.props.color,
    fontSize: s.fontSize,
    font: s.font,
    textColor: s.color,
    bold: s.bold,
    italic: s.italic,
    align: s.align,
    valign: s.valign ?? 'top',
    w: item.w,
    h: item.h,
  };
}

export function sameNoteLook(a: NoteLook, b: NoteLook): boolean {
  return (
    a.color === b.color &&
    a.fontSize === b.fontSize &&
    a.font === b.font &&
    a.textColor === b.textColor &&
    a.bold === b.bold &&
    a.italic === b.italic &&
    a.align === b.align &&
    a.valign === b.valign &&
    Math.abs(a.w - b.w) < 0.5 &&
    Math.abs(a.h - b.h) < 0.5
  );
}

/** Makes a note's look the default for new notes on its board (one undoable step). */
export function setBoardNoteLook(board: BoardDoc, item: NoteItem): void {
  board.setMeta({ [NOTE_LOOK_KEY]: lookOfNote(item) });
}

export function resetBoardNoteLook(board: BoardDoc): void {
  board.setMeta({ [NOTE_LOOK_KEY]: null });
}

/** Props of a new note with this look. Styles at their built-in value are left out, as notes never stored them. */
export function notePropsFor(look: NoteLook, text: string): NoteItem['props'] {
  const props: NoteItem['props'] = { text, color: look.color, fontSize: look.fontSize };
  if (look.font) props.font = look.font;
  if (look.textColor) props.textColor = look.textColor;
  if (look.bold) props.bold = true;
  if (look.italic) props.italic = true;
  if (look.align !== 'left') props.align = look.align;
  if (look.valign !== 'top') props.valign = look.valign;
  return props;
}
